import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { DateRangePickerProps } from 'components/DateRangePicker';
import { fixPagination } from 'utils';
import BasicPagination from './BasicPagination';
import PaginationLimitSelect from './PaginationLimitSelect';
import { StyledDateRangePicker, useStyles } from './styles';
import { ChangeListener, PaginationLabels, Value } from './types';
import { getPaginationSetter } from './utils';
import { DEFAULT_PAGINATION_VALUE } from './constants';
import AccountTypeSelect, { AccountTypeSelectProps } from './AccountTypeSelect';

export type PaginationWithDateRangeProps = {
  value?: Value;
  defaultValue?: Value;
  count: number;
  labels?: PaginationLabels;
  dateRangePickerProps: DateRangePickerProps;
  accountTypeSelectProps: AccountTypeSelectProps;
  onChange?: ChangeListener;
  accountTypeSelect?: ReactNode;
};

const PaginationWithDateRange: FC<PaginationWithDateRangeProps> = ({
  value,
  defaultValue,
  count = 0,
  onChange,
  labels,
  dateRangePickerProps,
  accountTypeSelectProps,
}) => {
  const styles = useStyles();
  const [innerValue, setInnerValue] = useState(null as unknown as Value);
  const selectedValue = useMemo(
    () => value || innerValue || defaultValue || DEFAULT_PAGINATION_VALUE,
    [defaultValue, innerValue, value],
  );

  const applyChanges: ChangeListener = useCallback(
    (newValue: Value) => {
      setInnerValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  const setPagination = useMemo(() => getPaginationSetter(applyChanges, selectedValue), [applyChanges, selectedValue]);

  return (
    <div className={`${styles.paginationWrapperInnerContent} ${styles.paginationWrapperInnerContentPadding}`}>
      <AccountTypeSelect {...accountTypeSelectProps} />
      <StyledDateRangePicker {...dateRangePickerProps} />
      <PaginationLimitSelect
        value={selectedValue.limit}
        count={count}
        label={labels?.limitSelectLabel}
        onChange={(selected): void => setPagination(fixPagination({ ...selectedValue, limit: selected }, count))}
      />
      <BasicPagination value={selectedValue} count={count} label={labels?.itemCountLabel} onChange={setPagination} />
    </div>
  );
};

export default PaginationWithDateRange;
